import * as React from 'react';
import { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';
import dayjs from 'dayjs';

import { FilterTriggerProps, FilterValue, FilterValueType } from '../types';
import FilterInputTrigger from './FilterInputTrigger';

const FilterTrigger: React.FC<FilterTriggerProps> = ({
  type = 'input',
  dropdownType,
  dropdownVisible,
  value,
  noneValue,
  options,
  needBackFill,
  datePickerProps,
  customBackFill,
  onInputChange,
  onChange,
  ...restProps
}) => {
  const [inputValue, setInputValue] = useState('');
  const [changedValue, setChangedValue] = useState<FilterValue>('');
  const [backFillText, setBackFillText] = useState<string>('');
  const isInputType = type === 'input';
  const isSearchType = type === 'search';
  const isSelectType = type === 'select';
  const isDatePickerType = type === 'date-picker';
  const isWithDropdown = !!dropdownType;
  const isSelectTypeTriggerSelected =
    dropdownType === 'multiple' ? !!(changedValue as any[])?.length : undefined;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    onInputChange?.(e.target.value);
  };

  const handleClear = (e: React.MouseEvent<HTMLElement>) => {
    if (isWithDropdown && !dropdownVisible) e.stopPropagation();
    setInputValue('');
    setChangedValue(noneValue);
    onInputChange?.('');
    onChange?.(noneValue);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (isInputType && isWithDropdown) return;
    if (e.key === 'Enter') {
      setChangedValue(inputValue);
      onChange?.(inputValue);
    }
  };

  useEffect(() => {
    if (isSearchType) needBackFill = true;
    if (needBackFill && (isSearchType || isSelectType || isDatePickerType)) {
      const setFunction = isSearchType ? setInputValue : setBackFillText;
      if (customBackFill) {
        const backFillText = customBackFill(value || '');
        setFunction(backFillText);
      } else if (isDatePickerType) {
        if (value && dayjs.isDayjs(value)) {
          const format =
            typeof datePickerProps?.format === 'string' ? datePickerProps.format : 'MM/DD/YYYY';
          setFunction(dayjs(value).format(format));
        } else {
          setFunction('');
        }
      } else {
        if (dropdownType === 'single') {
          const selectedOption = options?.find((option) => option.value === value);
          setFunction(selectedOption ? String(selectedOption.label) : (value as string));
        } else if (dropdownType === 'multiple') {
          const selectedOptions = options?.filter((option) =>
            (value as FilterValueType[])?.includes(option.value),
          );
          const selectedLabels = selectedOptions?.map((option) => option.label);
          setFunction(selectedLabels ? selectedLabels.join(',') : (value as string));
        }
      }
    }
    if (value !== changedValue) {
      // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
      if (isInputType) setInputValue((value || '') as string);
      // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
      setChangedValue(value);
    }
  }, [value]);

  return isSelectType || isDatePickerType ? (
    <FilterInputTrigger
      {...restProps}
      readOnly={true}
      value={changedValue as string}
      inputValue={backFillText as string}
      selected={isSelectTypeTriggerSelected}
      onClear={handleClear}
    />
  ) : (
    <FilterInputTrigger
      {...restProps}
      value={changedValue as string}
      inputValue={inputValue as string}
      onChange={handleChange}
      onClear={handleClear}
      onKeyDown={handleKeyDown}
    />
  );
};

export default FilterTrigger;
